const CART_BLOCK_ID = 'evereve-cart-block';
class SaveForLaterLineItem extends HTMLElement {
  constructor() {
    super();

    this.productId = this.getAttribute('product-id');
    this.variantId = this.getAttribute('variant-id');
    this.quantity = this.getAttribute('quantity') || 1;
    this.productUrl = this.getAttribute('product-url');

    this.addToCartButton = this.querySelector('.atc-action');
    this.removeButton = this.querySelector('[aria-label="Remove"]');

    this.sfl = null;

    this.shopifySectionId = this.getShopifySectionId();

    this.handleAddToCart = this.handleAddToCart.bind(this);
    this.handleRemoveFromList = this.handleRemoveFromList.bind(this);
    this.removeFromSaveForLater = this.removeFromSaveForLater.bind(this);
  }

  connectedCallback() {
    if (!this.addToCartButton || !this.removeButton) {
      console.error(
        'SaveForLaterLineItem requires both Add to Cart and Remove buttons.',
      );
      return;
    }

    if (!this.validateAttributes()) {
      return;
    }

    this.addToCartButton.addEventListener('click', this.handleAddToCart);
    this.removeButton.addEventListener('click', this.handleRemoveFromList);
  }

  disconnectedCallback() {
    this.addToCartButton.removeEventListener('click', this.handleAddToCart);
    this.removeButton.removeEventListener('click', this.handleRemoveFromList);
  }

  getShopifySectionId() {
    const section = this.closest('[id^="shopify-section"]');
    if (section) {
      return section.id.replace('shopify-section-', '');
    }
    return null;
  }

  validateAttributes() {
    if (!this.productId || !this.variantId || !this.productUrl) {
      console.error(
        `SaveForLaterLineItem is missing required attributes: 
        productId: ${this.productId}, 
        variantId: ${this.variantId}, 
        productUrl: ${this.productUrl}`,
      );
      return false;
    }
    return true;
  }

  async initSwym() {
    if (typeof SaveForLater === 'undefined' || !window.SaveForLaterInstance) {
      console.error('SaveForLater instance is not available.');
      this.hideButton();
      return false;
    }

    this.sfl = window.SaveForLaterInstance;
    return true;
  }

  async handleAddToCart() {
    if (!this.validateAttributes()) return;

    const formData = {
      items: [
        {
          id: Number(this.variantId),
          quantity: Number(this.quantity),
        },
      ],
    };

    this.addToCartButton.disabled = true;

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!response.ok) throw new Error('Failed to add to cart');

      await response.json();

      this.updateButtonState(this.addToCartButton, 'Added!', 'success');

      this.refreshCartContent();

      await this.removeFromSaveForLater();
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.updateButtonState(this.addToCartButton, 'Error!', 'error');
    } finally {
      this.addToCartButton.disabled = false;
      document.dispatchEvent(new Event('cart:refresh'));
    }
  }

  handleRemoveFromList() {
    this.removeFromSaveForLater();
  }

  // eslint-disable-next-line class-methods-use-this
  refreshCartContent() {
    fetch(`/cart?section_id=${this.shopifySectionId}`)
      .then((response) => {
        if (!response.ok) {
          throw new Error('refresh evereve-cart response was not ok');
        }
        return response.text();
      })
      .then((html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const updatedCartContent = doc.getElementById(CART_BLOCK_ID);

        const cartBlock = document.getElementById(CART_BLOCK_ID);
        if (cartBlock && updatedCartContent) {
          cartBlock.innerHTML = updatedCartContent.innerHTML;
        } else {
          console.error(`Could not find ${CART_BLOCK_ID} in the response or the DOM.`);
        }
      })
      .catch((error) => {
        console.error('Error refreshing the evereve-cart:', error);
      })
      .finally(() => {
        const initEvent = new CustomEvent('EE:sfl:ready', { bubbles: false });
        document.dispatchEvent(initEvent);
      });
  }

  async removeFromSaveForLater() {
    if (!this.sfl) {
      const initialized = await this.initSwym();
      if (!initialized) return;
    }
    const products = [
      {
        du: this.productUrl,
        empi: Number(this.productId),
        epi: Number(this.variantId),
      },
    ];

    try {
      document.documentElement.dispatchEvent(
        new CustomEvent('theme:loading:start', { bubbles: true }),
      );

      await this.sfl.remove(products);

      const cartChangeEvent = new Event('cart:refresh');
      document.dispatchEvent(cartChangeEvent);

      document.documentElement.dispatchEvent(
        new CustomEvent('theme:loading:end', { bubbles: true }),
      );

      const parentRow = this.addToCartButton.closest('tr');
      parentRow.classList.add('fade-out');

      parentRow.addEventListener('transitionend', () => {
        parentRow.remove();
      });
    } catch (error) {
      console.error('Error removing product from SFL List:', error);
    }
  }

  // eslint-disable-next-line class-methods-use-this
  updateButtonState(button, text, state) {
    if (button) {
      button.textContent = text;

      if (state === 'error') {
        button.classList.add('error');
        setTimeout(() => button.classList.remove('error'), 5000);
      } else if (state === 'success') {
        button.classList.add('success');
        setTimeout(() => button.classList.remove('success'), 2000);
      }
    }
  }

  get productData() {
    return {
      id: Number(this.productId),
      variantId: Number(this.variantId),
      quantity: Number(this.quantity),
      url: this.productUrl,
    };
  }
}

customElements.define('save-for-later-line-item', SaveForLaterLineItem);
